import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  MessageCircle, 
  Pin, 
  Search,
  Flame,
  Heart,
  HandMetal,
  Plus,
  Send,
  Trash2,
  Mail,
  CheckCircle,
  Loader2,
  X,
  Crown,
  ExternalLink
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { trackEvent } from '../utils/analytics';
import MiniAudioPlayer from '../components/MiniAudioPlayer';

const API_URL = '';  // Use relative URLs with proxy

// MCM Team Badge Component
const MCMTeamBadge = () => (
  <Badge className="bg-gradient-to-r from-[#F2D857] to-[#D4A847] text-black text-xs font-bold flex items-center gap-1">
    <Crown className="h-3 w-3" />
    MCM Team
  </Badge>
);

const Cafe = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'hot-seat' });
  
  // Reply state
  const [expandedPost, setExpandedPost] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const [postReplies, setPostReplies] = useState({});
  const replyInputRef = useRef(null);
  const postsContainerRef = useRef(null);  // Ref for posts section (for mobile scroll)
  
  // Current user state
  const [currentUser, setCurrentUser] = useState(null);
  const [artistUser, setArtistUser] = useState(null); // For artist community login
  
  // Artist login modal state
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [isRequestingLink, setIsRequestingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  
  // User reactions per post (tracked locally)
  const [userReactions, setUserReactions] = useState({});

  // Check URL for artist token on mount (magic link callback)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const artistToken = urlParams.get('artist_token');
    
    if (artistToken) {
      verifyArtistToken(artistToken);
    } else {
      // Check for existing artist session
      const storedArtistToken = localStorage.getItem('artist_token');
      if (storedArtistToken) {
        fetchArtistProfile(storedArtistToken);
      }
    }
  }, []);

  const verifyArtistToken = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/artist/verify?token=${token}`);
      if (response.data.success) {
        localStorage.setItem('artist_token', response.data.token);
        setArtistUser(response.data.artist);
        toast({
          title: "Welcome to the Cafe!",
          description: `Hey ${response.data.artist.name}, you can now join the conversation!`
        });
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Failed to verify artist token:', error);
      toast({
        title: "Link expired or invalid",
        description: "Please request a new login link.",
        variant: "destructive"
      });
    }
  };

  const fetchArtistProfile = async (token) => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/artist/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setArtistUser(response.data);
    } catch {
      // Token expired, clear it
      localStorage.removeItem('artist_token');
    }
  };

  // Check if user is authenticated (reviewer OR artist)
  const isAuthenticated = () => {
    return currentUser || artistUser;
  };

  // Get the display name for current user
  const getCurrentUserName = () => {
    if (currentUser) return currentUser.name || 'Reviewer';
    if (artistUser) return artistUser.name || 'Artist';
    return null;
  };

  // Get auth header for API calls
  const getAuthHeader = () => {
    const reviewerToken = localStorage.getItem('access_token');
    const artistToken = localStorage.getItem('artist_token');
    
    if (reviewerToken) return { 'Authorization': `Bearer ${reviewerToken}` };
    if (artistToken) return { 'Authorization': `Bearer ${artistToken}` };
    return {};
  };
  
  // Check if current user can moderate (moderator role or admin only)
  const canModerate = () => {
    // Check for admin token (from admin dashboard)
    const adminToken = localStorage.getItem('admin_access_token');
    if (adminToken) return true;
    
    // Check for reviewer with moderator/admin role
    if (!currentUser) return false;
    const roles = currentUser.roles || [];
    return roles.includes('moderator') || currentUser.role === 'admin';
  };
  
  // Check if current user is admin (can pin/unpin)
  const isAdmin = () => {
    const adminToken = localStorage.getItem('admin_access_token');
    if (adminToken) return true;
    if (!currentUser) return false;
    return currentUser.role === 'admin';
  };
  
  // Check if current user is the author
  const isAuthor = (authorId) => {
    if (currentUser && currentUser.id === authorId) return true;
    if (artistUser && artistUser.id === authorId) return true;
    return false;
  };
  
  // Check if user can delete a post/reply
  const canDelete = (authorId) => {
    return isAuthor(authorId) || canModerate();
  };
  
  // Fetch current reviewer user on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCurrentUser(response.data);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    
    fetchCurrentUser();
  }, []);

  // State to track degraded data
  const [isDegraded, setIsDegraded] = useState(false);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = {
        category: selectedCategory !== 'all' ? selectedCategory : undefined,
        search: searchTerm || undefined
      };
      const response = await axios.get(`${API_URL}/api/forum/posts`, { params });
      
      // Check for degraded response (Cafe is a degradable endpoint)
      if (response.data.degraded) {
        setIsDegraded(true);
        // Use the degraded data but keep previous if available
        const fallbackPosts = response.data.data?.posts || posts;
        setPosts(fallbackPosts);
        console.warn('📉 Cafe showing cached data:', response.data.degraded_reason);
      } else {
        setIsDegraded(false);
        setPosts(response.data.posts || []);
      }
      
      // Fetch user reactions for all posts in parallel for speed
      const postsToProcess = response.data.degraded ? (response.data.data?.posts || []) : (response.data.posts || []);
      if (postsToProcess.length > 0) {
        const reactionPromises = postsToProcess.map(post => 
          axios.get(`${API_URL}/api/forum/posts/${post.id}/my-reactions`)
            .then(res => ({ postId: post.id, reactions: res.data.reactions || [] }))
            .catch(() => ({ postId: post.id, reactions: [] }))
        );
        const results = await Promise.all(reactionPromises);
        const newUserReactions = {};
        results.forEach(({ postId, reactions }) => {
          newUserReactions[postId] = reactions;
        });
        setUserReactions(newUserReactions);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      // Keep existing posts on error (graceful degradation)
      if (posts.length === 0) {
        setPosts([]);
      }
      setIsDegraded(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/forum/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    
    fetchCategories();
    fetchPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, searchTerm]);

  // Request artist magic link
  const handleRequestMagicLink = async () => {
    if (!loginEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter your email address",
        variant: "destructive"
      });
      return;
    }

    setIsRequestingLink(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/artist/request-magic-link`, {
        email: loginEmail.toLowerCase().trim()
      });
      
      if (response.data.success) {
        setLinkSent(true);
        toast({
          title: "Check your email!",
          description: response.data.message
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to send login link. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRequestingLink(false);
    }
  };

  // Show login modal when unauthenticated user tries to comment
  const promptLogin = () => {
    setShowLoginModal(true);
    setLinkSent(false);
    setLoginEmail('');
  };

  const handleCreatePost = async () => {
    if (!isAuthenticated()) {
      promptLogin();
      return;
    }

    if (!newPost.title || !newPost.content) {
      toast({
        title: "Missing fields",
        description: "Please fill in both title and content",
        variant: "destructive"
      });
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/forum/posts`,
        newPost,
        { headers: getAuthHeader() }
      );

      // Track cafe post event
      trackEvent('cafe_post', { category: newPost.category });

      toast({
        title: "Post created!",
        description: "Your post has been published"
      });

      setNewPost({ title: '', content: '', category: 'hot-seat' });
      setShowNewPostForm(false);
      fetchPosts();
    } catch (error) {
      toast({
        title: "Failed to create post",
        description: error.response?.data?.detail || "Please try again",
        variant: "destructive"
      });
    }
  };

  // Handle reaction - PUBLIC (no auth required, IP-tracked, ONE reaction per user)
  const handleReaction = async (postId, reactionType) => {
    // Track cafe reaction event
    trackEvent('cafe_react', { reaction_type: reactionType, post_id: postId });

    try {
      const response = await axios.post(
        `${API_URL}/api/forum/posts/${postId}/reactions`,
        { type: reactionType }
      );
      
      const action = response.data.action;
      
      // Optimistic update - update local state immediately
      if (action === 'removed') {
        // Toggled off same reaction
        setUserReactions(prev => ({ ...prev, [postId]: [] }));
        setPosts(prev => prev.map(p => {
          if (p.id === postId) {
            return {
              ...p,
              reactions: {
                ...p.reactions,
                [reactionType]: Math.max(0, (p.reactions?.[reactionType] || 0) - 1)
              }
            };
          }
          return p;
        }));
      } else if (action === 'changed') {
        // Swapped reaction
        const oldReaction = response.data.from;
        setUserReactions(prev => ({ ...prev, [postId]: [reactionType] }));
        setPosts(prev => prev.map(p => {
          if (p.id === postId) {
            return {
              ...p,
              reactions: {
                ...p.reactions,
                [oldReaction]: Math.max(0, (p.reactions?.[oldReaction] || 0) - 1),
                [reactionType]: (p.reactions?.[reactionType] || 0) + 1
              }
            };
          }
          return p;
        }));
      } else if (action === 'added') {
        // New reaction
        setUserReactions(prev => ({ ...prev, [postId]: [reactionType] }));
        setPosts(prev => prev.map(p => {
          if (p.id === postId) {
            return {
              ...p,
              reactions: {
                ...p.reactions,
                [reactionType]: (p.reactions?.[reactionType] || 0) + 1
              }
            };
          }
          return p;
        }));
      }
      // No need to fetchPosts() - we updated locally for instant feedback
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      if (error.response?.data?.detail === 'You have already added this reaction') {
        // Already reacted, update local state
        setUserReactions(prev => ({
          ...prev,
          [postId]: [...(prev[postId] || []), reactionType]
        }));
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to add reaction",
          variant: "destructive"
        });
      }
    }
  };

  // Fetch replies for a post
  const fetchReplies = async (postId) => {
    try {
      const response = await axios.get(`${API_URL}/api/forum/posts/${postId}/replies`);
      setPostReplies(prev => ({ ...prev, [postId]: response.data.replies || [] }));
    } catch (error) {
      console.error('Failed to fetch replies:', error);
    }
  };

  // Toggle reply section
  const toggleReplies = (postId) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
    } else {
      setExpandedPost(postId);
      if (!postReplies[postId]) {
        fetchReplies(postId);
      }
    }
  };

  // Submit a reply - REQUIRES AUTH
  const handleSubmitReply = async (postId) => {
    if (!isAuthenticated()) {
      promptLogin();
      return;
    }

    if (!replyContent.trim()) {
      toast({
        title: "Empty reply",
        description: "Please enter some text for your reply",
        variant: "destructive"
      });
      return;
    }

    setIsSubmittingReply(true);
    try {
      await axios.post(
        `${API_URL}/api/forum/posts/${postId}/replies`,
        { content: replyContent },
        { headers: getAuthHeader() }
      );
      
      // Track cafe comment event for analytics
      trackEvent('cafe_comment', { post_id: postId });
      
      setReplyContent('');
      fetchReplies(postId);
      // Optimistically update reply count instead of fetching all posts
      setPosts(prev => prev.map(p => {
        if (p.id === postId) {
          return { ...p, replies: (p.replies || 0) + 1 };
        }
        return p;
      }));
      toast({
        title: "Reply posted!",
        description: "Your reply has been added"
      });
    } catch (error) {
      console.error('Failed to post reply:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to post reply. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingReply(false);
    }
  };

  // Delete a post (author or moderator only)
  const handleDeletePost = async (postId) => {
    if (!isAuthenticated()) {
      promptLogin();
      return;
    }

    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/forum/posts/${postId}`,
        { headers: getAuthHeader() }
      );
      toast({
        title: "Post deleted",
        description: "The post has been removed"
      });
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete post:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete post",
        variant: "destructive"
      });
    }
  };

  // Pin/Unpin a post (admin only)
  const handleTogglePin = async (postId) => {
    // Get admin token for auth
    const adminToken = localStorage.getItem('admin_access_token');
    if (!adminToken) {
      toast({
        title: "Admin access required",
        description: "You must be logged in as an admin to pin/unpin posts",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.put(
        `${API_URL}/api/forum/posts/${postId}/pin`,
        {},
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );
      
      const action = response.data.is_pinned ? 'pinned' : 'unpinned';
      toast({
        title: `Post ${action}`,
        description: `The post has been ${action} successfully`
      });
      
      // Update local state optimistically
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.id === postId 
            ? { ...post, is_pinned: response.data.is_pinned }
            : post
        ).sort((a, b) => {
          // Re-sort: pinned posts first, then by date
          if (a.is_pinned && !b.is_pinned) return -1;
          if (!a.is_pinned && b.is_pinned) return 1;
          return new Date(b.created_at) - new Date(a.created_at);
        })
      );
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to pin/unpin post",
        variant: "destructive"
      });
    }
  };

  // Delete a reply (author or moderator only)
  const handleDeleteReply = async (postId, replyId) => {
    if (!isAuthenticated()) {
      promptLogin();
      return;
    }

    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/api/forum/posts/${postId}/replies/${replyId}`,
        { headers: getAuthHeader() }
      );
      toast({
        title: "Reply deleted",
        description: "The reply has been removed"
      });
      fetchReplies(postId);
      fetchPosts();
    } catch (error) {
      console.error('Failed to delete reply:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete reply",
        variant: "destructive"
      });
    }
  };

  // Logout artist
  const handleArtistLogout = () => {
    localStorage.removeItem('artist_token');
    setArtistUser(null);
    toast({
      title: "Logged out",
      description: "You have been logged out of the community"
    });
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Just now';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <>
      <Helmet>
        <title>The Millionaire Cafe | Mic Check Millionaire Community</title>
        <meta 
          name="description" 
          content="Join The Millionaire Cafe - Mic Check Millionaire's community for artists and reviewers. Get feedback, share strategies, connect with creators, and grow your music income."
        />
        <meta 
          name="keywords" 
          content="Mic Check Millionaire, Millionaire Cafe, music review community, artist feedback, live music reviews, music business growth, artist collaboration, music monetization, music discovery platform, reviewer network"
        />
        
        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content="The Millionaire Cafe | Mic Check Millionaire" />
        <meta 
          property="og:description" 
          content="Your creative headquarters for collaboration, feedback, and growth. Connect with artists and reviewers in the music community."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="/logo-og.png" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Millionaire Cafe | Mic Check Millionaire" />
        <meta 
          name="twitter:description" 
          content="Join the community for music feedback, collaboration, and growth strategies."
        />
        
        {/* Structured Data for Google */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DiscussionForumPosting",
            "name": "The Millionaire Cafe",
            "description": "Community forum for music artists and reviewers to collaborate and grow",
            "url": window.location.href,
            "discussionUrl": window.location.href,
            "interactionStatistic": {
              "@type": "InteractionCounter",
              "interactionType": "https://schema.org/CommentAction",
              "userInteractionCount": posts.length
            }
          })}
        </script>
      </Helmet>
      
      {/* Join Conversation Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="sm:max-w-md">
          {/* Close button is built into DialogContent */}
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <MessageCircle className="h-5 w-5 text-[#F2D857]" />
              Join the Conversation
            </DialogTitle>
            <DialogDescription className="text-gray-600 text-center">
              Connect with artists and reviewers in our community.
            </DialogDescription>
          </DialogHeader>
          
          {!linkSent ? (
            <div className="space-y-5 pt-2">
              {/* Primary CTA - Become a Reviewer */}
              <div className="bg-gradient-to-r from-[#F2D857]/10 to-[#F2D857]/5 border-2 border-[#F2D857] rounded-xl p-4 text-center">
                <h3 className="font-bold text-gray-900 mb-1">Earn money reviewing music</h3>
                <p className="text-sm text-gray-600 mb-3">
                  Become a reviewer and get paid for your feedback.
                </p>
                <a
                  href="/signup"
                  className="inline-flex items-center justify-center w-full bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold py-2.5 px-4 rounded-lg transition-colors"
                  data-testid="become-reviewer-btn"
                >
                  Become a Reviewer
                </a>
              </div>
              
              {/* Divider */}
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-2 text-gray-500">Or for artists</span>
                </div>
              </div>
              
              {/* Secondary - Artist Magic Link */}
              <div className="space-y-3">
                <p className="text-sm text-gray-600 text-center">
                  Already on the artist waitlist? Enter your email for instant access.
                </p>
                <div className="space-y-2">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleRequestMagicLink()}
                    className="border-2 focus:border-[#F2D857] text-center"
                    data-testid="artist-login-email"
                  />
                  <Button
                    onClick={handleRequestMagicLink}
                    disabled={isRequestingLink}
                    variant="outline"
                    className="w-full border-2 border-gray-300 hover:border-[#F2D857] hover:bg-[#F2D857]/10"
                    data-testid="artist-login-submit"
                  >
                    {isRequestingLink ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Magic Link
                      </>
                    )}
                  </Button>
                </div>
                
                <p className="text-xs text-gray-500 text-center pt-1">
                  Not on the waitlist?{' '}
                  <a href="/artist-coming-soon?redirect=/cafe" className="text-[#F2D857] hover:underline font-medium">
                    Join the artist waitlist
                  </a>
                </p>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-gray-900">Check your email!</p>
                <p className="text-sm text-gray-500 mt-1">
                  We sent a login link to <strong>{loginEmail}</strong>
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => {
                  setLinkSent(false);
                  setLoginEmail('');
                }}
                className="mt-4"
              >
                Try a different email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      <div className="min-h-screen bg-gray-50">
        {/* Degraded State Banner - Non-invasive */}
        {isDegraded && (
          <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-center">
            <p className="text-sm text-yellow-800">
              ⚡ Reconnecting... Showing last known data.
            </p>
          </div>
        )}
        
        {/* Header */}
        <div className="bg-black text-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold font-montserrat mb-4">
                The Millionaire Cafe
              </h1>
              <p className="text-xl text-gray-300">
                Connect, share, and grow with the Mic Check Millionaire community
              </p>
              
              {/* User Status */}
              {isAuthenticated() && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <Badge className="bg-green-600 text-white">
                    Logged in as {getCurrentUserName()}
                  </Badge>
                  {artistUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleArtistLogout}
                      className="text-gray-400 hover:text-white"
                    >
                      <X className="h-4 w-4 mr-1" />
                      Logout
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Search Bar */}
            <Card className="mb-6 p-4 border-2 border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />
                  <Input
                    type="text"
                    placeholder="Search posts by title or content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchPosts()}
                    className="pl-10 h-11 text-base w-full min-w-0"
                    data-testid="cafe-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={fetchPosts} 
                    variant="outline" 
                    className="h-11 px-4"
                    data-testid="cafe-search-btn"
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                  <Button 
                    onClick={() => isAuthenticated() ? setShowNewPostForm(!showNewPostForm) : promptLogin()}
                    className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold h-11 flex-1 sm:flex-none"
                    data-testid="cafe-new-post-btn"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Post
                  </Button>
                </div>
              </div>
            </Card>

            {/* Category Filter Tags */}
            <div className="mb-6">
              {/* Desktop: Horizontal wrap layout */}
              <div className="hidden sm:flex flex-wrap gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`
                      group relative inline-flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm
                      transition-all duration-300 ease-in-out
                      ${selectedCategory === category.id
                        ? 'bg-[#F2D857] text-black shadow-lg shadow-[#F2D857]/30 scale-105'
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-[#F2D857] hover:text-black hover:shadow-md hover:shadow-[#F2D857]/20 hover:scale-102'
                      }
                    `}
                    data-testid={`category-${category.id}`}
                  >
                    <span className="transition-transform duration-300 group-hover:scale-110">{category.emoji}</span>
                    <span>{category.name}</span>
                    
                    {/* Tooltip on Hover */}
                    <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 shadow-xl w-48 text-center">
                      {category.description}
                      <span className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-800"></span>
                    </span>
                  </button>
                ))}
              </div>
              
              {/* Mobile: Vertical stack layout */}
              <div className="sm:hidden flex flex-col gap-2">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => {
                      setSelectedCategory(category.id);
                      // Auto-scroll to posts on mobile after category selection
                      setTimeout(() => {
                        if (postsContainerRef.current) {
                          postsContainerRef.current.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                          });
                        }
                      }, 100);
                    }}
                    className={`
                      flex items-start gap-3 px-4 py-3 rounded-xl font-medium text-sm text-left
                      transition-all duration-200
                      ${selectedCategory === category.id
                        ? 'bg-[#F2D857] text-black shadow-md'
                        : 'bg-white text-gray-700 border-2 border-gray-200'
                      }
                    `}
                  >
                    <span className="text-lg flex-shrink-0">{category.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold">{category.name}</div>
                      <div className={`text-xs mt-0.5 ${selectedCategory === category.id ? 'text-black/70' : 'text-gray-500'}`}>
                        {category.description}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* New Post Form */}
            {showNewPostForm && (
              <Card className="mb-6 p-6 border-2 border-[#F2D857]">
                <h3 className="text-xl font-bold mb-4">Create New Post</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Category</label>
                    <select
                      value={newPost.category}
                      onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#F2D857] focus:outline-none"
                      data-testid="new-post-category"
                    >
                      {categories.filter(c => c.id !== 'all').map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.emoji} {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <Input
                      placeholder="What's on your mind?"
                      value={newPost.title}
                      onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                      className="border-2 focus:border-[#F2D857]"
                      data-testid="new-post-title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Content</label>
                    <Textarea
                      placeholder="Share your thoughts..."
                      value={newPost.content}
                      onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                      rows={4}
                      className="border-2 focus:border-[#F2D857]"
                      data-testid="new-post-content"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={handleCreatePost}
                      className="bg-[#F2D857] hover:bg-[#F2D857]/90 text-black font-bold"
                      data-testid="new-post-submit"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Post
                    </Button>
                    <Button
                      onClick={() => setShowNewPostForm(false)}
                      variant="outline"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            {/* Posts List */}
            <div ref={postsContainerRef}>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#F2D857] border-r-transparent"></div>
                <p className="mt-4 text-gray-600">Loading discussions...</p>
              </div>
            ) : posts.length === 0 ? (
              <Card className="p-12 text-center border-2 border-gray-200">
                <MessageCircle className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-bold text-gray-600 mb-2">No posts yet</h3>
                <p className="text-gray-500">Be the first to start a conversation!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <Card 
                    key={post.id} 
                    className={`p-6 border-2 hover:border-[#F2D857] transition-colors ${post.is_pinned ? 'border-[#F2D857] bg-[#F2D857]/5' : 'border-gray-200'}`}
                    data-testid={`post-${post.id}`}
                  >
                    {/* Post Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={post.author?.avatar} />
                        <AvatarFallback className={`${post.author?.is_mcm_team ? 'bg-gradient-to-r from-[#F2D857] to-[#D4A847]' : 'bg-[#F2D857]'} text-black font-bold`}>
                          {post.author?.is_mcm_team ? 'M' : post.author?.name?.[0]?.toUpperCase() || 'A'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-bold">{post.author?.name || 'Community Member'}</h4>
                          {post.author?.is_mcm_team && <MCMTeamBadge />}
                          {post.author?.user_type === 'reviewer' && !post.author?.is_mcm_team && (
                            <Badge className="bg-[#F2D857] text-black text-xs">Reviewer</Badge>
                          )}
                          {post.author?.user_type === 'artist_community' && (
                            <Badge className="bg-purple-600 text-white text-xs">Artist</Badge>
                          )}
                          {post.is_pinned && (
                            <Pin className="h-4 w-4 text-[#F2D857]" title="Pinned Post" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {formatTimestamp(post.created_at)}
                          {post.category && post.category !== 'general' && (
                            <>
                              {' • '}
                              <span className="text-[#F2D857]">
                                {categories.find(c => c.id === post.category)?.emoji} {categories.find(c => c.id === post.category)?.name}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      {/* Admin Controls: Pin/Unpin and Delete */}
                      <div className="flex items-center gap-1">
                        {/* Pin/Unpin Button (Admin only) */}
                        {isAdmin() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTogglePin(post.id)}
                            className={`${post.is_pinned ? 'text-[#F2D857] hover:text-gray-500' : 'text-gray-400 hover:text-[#F2D857]'} hover:bg-[#F2D857]/10`}
                            data-testid={`pin-post-${post.id}`}
                            title={post.is_pinned ? 'Unpin post' : 'Pin post'}
                          >
                            <Pin className="h-4 w-4" />
                          </Button>
                        )}
                        {/* Delete Post Button */}
                        {canDelete(post.author?.id) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePost(post.id)}
                            className="text-gray-400 hover:text-red-500 hover:bg-red-50"
                            data-testid={`delete-post-${post.id}`}
                            title="Delete post"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Post Content */}
                    <h3 className="text-xl font-bold mb-2">{post.title}</h3>
                    {/* For Hot Seat posts with metadata, clean up legacy markdown from content */}
                    {post.hot_seat_meta ? (
                      <p className="text-gray-700 mb-4 whitespace-pre-wrap">
                        {post.content
                          .split('\n')
                          .filter(line => 
                            !line.includes('[Listen on streaming platform]') && 
                            !line.includes('[Watch the review live') &&
                            !line.includes('Watch the review live here:') &&
                            !line.includes('🎵') &&
                            !line.includes('👀')
                          )
                          .join('\n')
                          .replace(/\*\*Genre:\*\*/g, 'Genre:')
                          .trim()
                        }
                      </p>
                    ) : (
                      <p className="text-gray-700 mb-4 whitespace-pre-wrap">{post.content}</p>
                    )}
                    
                    {/* Hot Seat Post: Audio Player & Links */}
                    {post.hot_seat_meta && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                        {/* Audio Player */}
                        <MiniAudioPlayer
                          audioUrl={post.hot_seat_meta.audio_url}
                          streamingLink={post.hot_seat_meta.streaming_link}
                          trackTitle={post.hot_seat_meta.track_title}
                          artistName={post.hot_seat_meta.artist_name}
                        />
                        
                        {/* Watch Live Review Link - Congruent with player buttons */}
                        {post.hot_seat_meta.session_url && (
                          <a
                            href={post.hot_seat_meta.session_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-3 bg-[#F2D857] text-black font-semibold rounded-lg hover:bg-[#F2D857]/90 transition-colors text-sm"
                            data-testid={`hotseat-session-link-${post.id}`}
                          >
                            <Flame className="w-5 h-5" />
                            Watch the Review Live
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    )}

                    {/* Reactions - PUBLIC (no auth required) - Horizontal layout */}
                    <div className="flex items-center gap-2 sm:gap-4 pt-4 border-t border-gray-200">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(post.id, 'fire')}
                        className={`px-2 sm:px-3 hover:bg-orange-50 hover:text-orange-600 ${(userReactions[post.id] || []).includes('fire') ? 'bg-orange-100 text-orange-600' : ''}`}
                        data-testid={`react-fire-${post.id}`}
                      >
                        <Flame className="h-4 w-4 sm:mr-1" />
                        <span className="text-xs sm:text-sm">{post.reactions?.fire || 0}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(post.id, 'heart')}
                        className={`px-2 sm:px-3 hover:bg-red-50 hover:text-red-600 ${(userReactions[post.id] || []).includes('heart') ? 'bg-red-100 text-red-600' : ''}`}
                        data-testid={`react-heart-${post.id}`}
                      >
                        <Heart className="h-4 w-4 sm:mr-1" />
                        <span className="text-xs sm:text-sm">{post.reactions?.heart || 0}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReaction(post.id, 'clap')}
                        className={`px-2 sm:px-3 hover:bg-purple-50 hover:text-purple-600 ${(userReactions[post.id] || []).includes('clap') ? 'bg-purple-100 text-purple-600' : ''}`}
                        data-testid={`react-clap-${post.id}`}
                      >
                        <HandMetal className="h-4 w-4 sm:mr-1" />
                        <span className="text-xs sm:text-sm">{post.reactions?.clap || 0}</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleReplies(post.id)}
                        className={`px-2 sm:px-3 hover:bg-blue-50 hover:text-blue-600 ml-auto ${expandedPost === post.id ? 'bg-blue-50 text-blue-600' : ''}`}
                        data-testid={`toggle-replies-${post.id}`}
                      >
                        <MessageCircle className="h-4 w-4 sm:mr-1" />
                        <span className="text-xs sm:text-sm">{post.replies || 0}</span>
                        <span className="hidden sm:inline ml-1">replies</span>
                      </Button>
                    </div>
                    
                    {/* Reply Section */}
                    {expandedPost === post.id && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {/* Reply Input - Mobile optimized with fixed position approach */}
                        <div className="mb-4">
                          <div className="flex gap-2 items-center sm:items-start">
                            <div className="flex-1 relative">
                              <Input
                                ref={replyInputRef}
                                placeholder={isAuthenticated() ? "Write a reply..." : "Login to reply..."}
                                value={replyContent}
                                onChange={(e) => setReplyContent(e.target.value)}
                                onFocus={() => {
                                  if (!isAuthenticated()) {
                                    promptLogin();
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSubmitReply(post.id);
                                  }
                                }}
                                className="pr-12 sm:pr-4"
                                disabled={!isAuthenticated()}
                                data-testid={`reply-input-${post.id}`}
                              />
                              {/* Mobile send button inside input */}
                              <Button
                                onClick={() => handleSubmitReply(post.id)}
                                disabled={isSubmittingReply || !replyContent.trim() || !isAuthenticated()}
                                className="sm:hidden absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 bg-[#F2D857] hover:bg-[#F2D857]/90 text-black rounded-full"
                                data-testid={`reply-submit-mobile-${post.id}`}
                              >
                                {isSubmittingReply ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                            {/* Desktop send button */}
                            <Button
                              onClick={() => handleSubmitReply(post.id)}
                              disabled={isSubmittingReply || !replyContent.trim() || !isAuthenticated()}
                              className="hidden sm:flex bg-[#F2D857] hover:bg-[#F2D857]/90 text-black"
                              data-testid={`reply-submit-${post.id}`}
                            >
                              {isSubmittingReply ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Send className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                          {!isAuthenticated() && (
                            <p className="text-xs text-gray-500 mt-2">
                              <button 
                                onClick={promptLogin}
                                className="text-[#F2D857] hover:underline font-medium"
                              >
                                Login
                              </button>
                              {' '}to join the conversation
                            </p>
                          )}
                        </div>
                        
                        {/* Replies List */}
                        {postReplies[post.id]?.length > 0 ? (
                          <div className="space-y-3">
                            {postReplies[post.id].map((reply) => (
                              <div 
                                key={reply.id} 
                                className="flex gap-3 p-3 bg-gray-50 rounded-lg group"
                                data-testid={`reply-${reply.id}`}
                              >
                                <Avatar className="h-8 w-8 flex-shrink-0">
                                  <AvatarImage src={reply.author?.avatar} />
                                  <AvatarFallback className={`${reply.author?.is_mcm_team ? 'bg-gradient-to-r from-[#F2D857] to-[#D4A847]' : 'bg-gray-200'} text-xs`}>
                                    {reply.author?.is_mcm_team ? 'M' : reply.author?.name?.charAt(0) || '?'}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-sm text-gray-900">
                                      {reply.author?.name || 'Anonymous'}
                                    </span>
                                    {reply.author?.is_mcm_team && <MCMTeamBadge />}
                                    <span className="text-xs text-gray-400">
                                      {formatTimestamp(reply.created_at)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700 mt-1">{reply.content}</p>
                                </div>
                                {/* Delete Reply Button */}
                                {canDelete(reply.author?.id) && (
                                  <button
                                    onClick={() => handleDeleteReply(post.id, reply.id)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-red-500 p-1 flex-shrink-0"
                                    data-testid={`delete-reply-${reply.id}`}
                                    title="Delete reply"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400 text-center py-4">
                            No replies yet. Be the first to reply!
                          </p>
                        )}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Cafe;
